import { computed } from 'mobx';
import Promise from 'bluebird';
import moment from 'moment';

import api from 'core/util/api';
import $auth from 'app/stores/$auth';

import { DEFAULT_DATETIME_FORMAT } from 'core/util/dateUtils';
import { showSuccessToast } from 'core/components/toast';
import { addFilters } from 'app/stores/query/FilterUtils';
import {
  MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP,
  MITIGATION_TYPES,
  MITIGATION_UI_STATE_TO_API_STATES_MAP,
  MITIGATION_UI_STATES,
  mitigationDetailsToFilterKeysMap,
  mitigationMethodFieldToMitigationDetailsMap,
  PLATFORM_OPTIONS
} from '@kentik/ui-shared/mitigations/constants';
import { transformMitigationPredicateToFilter } from './mitigationsUtils';
import MitigationCollection from './MitigationCollection';
import ManualMitigationModel from './ManualMitigationModel';
import MitigationPlatformCollection from './MitigationPlatformCollection';
import MitigationMethodCollection from './MitigationMethodCollection';
import BGPAnnouncementCollection from './BGPAnnouncementCollection';

export class MitigationsStore {
  collection = new MitigationCollection();

  platformsCollection = new MitigationPlatformCollection();

  methodsCollection = new MitigationMethodCollection();

  mitigationActions = {};

  announcementCollection = new BGPAnnouncementCollection();

  getPlatformType = (value) => PLATFORM_OPTIONS.find((type) => type.value === value);

  getMethodsById = (methodIds) => this.methodsCollection.models.filter((method) => methodIds.includes(method.id));

  getMethodById = (methodId) => this.methodsCollection.modelById[methodId];

  fetchMitigation(id) {
    return api.get(`/api/ui/mitigations/${id}`).then((mitigationResponse) => {
      if (mitigationResponse && mitigationResponse.length === 1) {
        return mitigationResponse[0];
      }

      return undefined;
    });
  }

  fetchAdaptiveFlowspecPreview(body) {
    return api.post('/api/ui/mitigations/adaptiveFlowspec', { body });
  }

  getFlowspecPreviewQuery({ alertModel, flowspecDetails, context }) {
    // build the base query based on the insight (alarm) - this will get us the baseline for the query including inferred dimension values
    // const query = this.store.$insights.getInsightQuery(insight);
    const query = this.store.$alerting.getAlertQuery(alertModel);

    // clear the end time so that we're looking at current traffic up until this point
    query.ending_time = moment.utc().format(DEFAULT_DATETIME_FORMAT);
    // clear filters so we base them on the context we're in (alarm or mitigation)
    query.filters = null;

    // look through flowspec details, check what we infer and what we have configured values for in order to build the filters
    // if we have a mitigation context, we work with target object on the mitigation
    // if we are in an alert context, we use the alert object dimension to key part
    const targetObj = context.get ? context.get('targetObj') : context.targetObj;

    Object.keys(flowspecDetails).forEach((flowspecKey) => {
      const value = flowspecDetails[flowspecKey];

      if (value && value.infer) {
        const filterField = mitigationDetailsToFilterKeysMap[flowspecKey];
        const policyDimension = MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP[flowspecKey];
        const filterValue = alertModel
          ? alertModel.get('key.value')?.[policyDimension] || alertModel.dimensionToKeyPart?.[policyDimension]
          : targetObj[mitigationMethodFieldToMitigationDetailsMap[flowspecKey]];

        if (filterField && filterValue !== undefined) {
          addFilters(query, [
            {
              filterField,
              filterValue,
              operator: 'ILIKE'
            }
          ]);
        }
      } else if (value && value.value && !value.infer) {
        const nestedFilterGroup = {
          connector: 'Any',
          not: false,
          name: '',
          named: false,
          filterGroups: [],
          autoAdded: '',
          saved_filters: []
        };

        const { predicates, groups } = value.value;

        nestedFilterGroup.filters = predicates
          .map((predicate) =>
            transformMitigationPredicateToFilter(predicate, mitigationDetailsToFilterKeysMap, flowspecKey)
          )
          .filter(Boolean);

        if (groups && groups.length) {
          groups.forEach((group) => {
            const groupFilterGroup = {
              connector: 'All',
              not: false,
              filters: group.predicates
                .map((groupPredicate) =>
                  transformMitigationPredicateToFilter(groupPredicate, mitigationDetailsToFilterKeysMap, flowspecKey)
                )
                .filter(Boolean),
              name: '',
              named: false,
              filterGroups: [],
              autoAdded: '',
              saved_filters: []
            };

            nestedFilterGroup.filterGroups.push(groupFilterGroup);
          });
        }

        if (!query.filters) {
          query.filters = {
            connector: 'All',
            filters: [],
            filterGroups: []
          };
        }

        if (nestedFilterGroup.filterGroups.length > 0 || nestedFilterGroup.filters.length > 0) {
          query.filters.filterGroups.push(nestedFilterGroup);
        }
      }
    });

    return query;
  }

  async loadMitigationActions() {
    return api
      .get('/api/ui/mitigations/actions', { showErrorToast: false })
      .then((actions) => {
        if (actions && actions.stateToActions) {
          this.mitigationActions = actions.stateToActions;
        } else {
          this.mitigationActions = {};
        }
      })
      .catch(() => {
        // we have error logs in node for this particular problem so we'll investigate there
        // we do not want to expose to users internal api url path variables and so on.
        // at worst, they won't be able to use actions on their mitigations, but realistically, they won't get here
        // if they have properly configured mitigation platforms and methods OR they shouldn't run actions if the former isn't true
      });
  }

  loadActiveMitigations(filters = {}) {
    return this.loadMitigationActions().then(() => {
      const serverFilters = {
        lookback: 86400,
        states: [MITIGATION_UI_STATES.WAITING, MITIGATION_UI_STATES.FAILED, MITIGATION_UI_STATES.ACTIVE].flatMap(
          (state) => MITIGATION_UI_STATE_TO_API_STATES_MAP[state]
        ),
        ...filters
      };

      return this.collection.setServerFilter(serverFilters, { force: true });
    });
  }

  newManualMitigationModel = () => new ManualMitigationModel({ $mitigations: this });

  @computed
  get unassignedMitigationMethods() {
    const assignedMethodIds = this.platformsCollection?.platformMethods.map((platformMethod) => platformMethod.value);
    const unassigned = this.methodsCollection.unfiltered.filter(
      (method) => !assignedMethodIds.includes(method.id) && method.type
    );
    return unassigned;
  }

  @computed
  get manualMitigationOptions() {
    return (this.platformsCollection.platformMethods || [])
      .map((option) => ({
        ...option,
        value: `${option.platform.id}-${option.method.id}`,
        label: `${option.platform.get('name')} - ${option.method.get('name')}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  @computed
  get mitigationTypeOptions() {
    const showFlowspec = $auth.hasPermission('alerts.flowSpec.enabled', {
      overrideForSudo: false
    });
    const showAdaptiveFlowspec = $auth.hasPermission('alerts.mit2bgp.enabled', {
      overrideForSudo: false
    });
    const showAdvanced = $auth.hasPermission('protect.advancedEnabled', {
      overrideForSudo: false
    });

    return PLATFORM_OPTIONS.filter((option) => {
      if (option.value === MITIGATION_TYPES.FLOWSPEC) {
        return showFlowspec;
      }

      if (option.value === MITIGATION_TYPES.ADAPTIVE_FLOWSPEC) {
        return showAdaptiveFlowspec;
      }

      if (option.advanced) {
        return showAdvanced;
      }

      return true;
    });
  }

  getMitigationStatusColor(status) {
    const colorHash = {
      [MITIGATION_UI_STATES.INACTIVE]: 'muted',
      [MITIGATION_UI_STATES.ACTIVE]: 'success',
      [MITIGATION_UI_STATES.FAILED]: 'danger',
      [MITIGATION_UI_STATES.WAITING]: 'warning'
    };

    return colorHash[status] || 'muted';
  }

  mitigate({ actionConfig, alarm_state, mitigation_id }) {
    return api.post('/api/ui/mitigations/mitigate', { data: { action: actionConfig, alarm_state, mitigation_id } });
  }

  withdrawManyBGPAnnouncements(announcementModels = [], options = {}) {
    return Promise.map(announcementModels, (announcementModel) =>
      this.withdrawBGPAnnouncement(announcementModel, {
        showErrorToast: true,
        showSuccessToast: false,
        removeFromCollection: false
      })
    )
      .then(() => {
        if (options.showSuccessToast) {
          showSuccessToast(`${announcementModels.length} BGP announcements withdrawn successfully`);
        }
        if (options.removeFromCollection) {
          this.announcementCollection.remove(announcementModels.map(({ id }) => id));
        }
      })
      .catch((e) => {
        if (options.showErrorToast) {
          showSuccessToast(`Failed to withdraw ${announcementModels.length} BGP announcements: ${e.message}`);
        }
      });
  }

  withdrawBGPAnnouncement(announcementModel, options = {}) {
    const { deviceId, typeKey, matchBody = {} } = announcementModel;

    if (!deviceId) {
      return Promise.resolve([]);
    }

    return api
      .post('/api/ui/mitigations/announcements/withdraw', {
        showErrorToast: Boolean(options.showErrorToast),
        data: { deviceIds: [deviceId], withdraw: { [typeKey]: matchBody } }
      })
      .then(() => {
        if (options.showSuccessToast) {
          showSuccessToast('BGP announcement withdrawn successfully');
        }

        if (options.removeFromCollection) {
          this.announcementCollection.remove(announcementModel.id);
        }
      });
  }

  isDeviceInRTBHPlatform(device) {
    const platform = this.platformsCollection.models.find(
      (model) => model.type === 'RTBH' && model.devices.includes(`${device.id}`)
    );

    return !!platform;
  }

  getMethodOptionsByType(platformType) {
    const options = [];
    this.methodsCollection.models.forEach((model) => {
      const { name, id, type } = model.get();
      if (platformType === type) {
        options.push({
          label: name,
          value: id
        });
      }
    });
    return options;
  }

  getAssociatedMitigationPlatforms = (deviceId, allowedTypes) =>
    this.platformsCollection.models.filter((platform) => {
      if (allowedTypes && allowedTypes.length > 0) {
        if (!allowedTypes.includes(platform.get('type'))) {
          return false;
        }
      }

      return Array.isArray(platform.devices) && platform.devices.includes(`${deviceId}`);
    });

  getMitigationPlatformFromMethod = (methodId) =>
    this.platformsCollection.platformMethods.find((option) => option.value === `${methodId}`);

  getActiveMitigationsUsingPlatform(platformId) {
    return this.collection.filter((model) => model.get('platformID') === platformId);
  }

  // Optional platformId param to scope results to a stricter method/platform match
  getActiveMitigationsUsingMethod(methodId, platformId) {
    return this.collection.filter((model) => {
      const matchesMethod = model.get('methodID') === methodId;
      const matchesPlatform = platformId ? model.get('platformID') === platformId : true;

      return matchesMethod && matchesPlatform;
    });
  }

  async getActiveMitigationsUsingDevice(deviceID) {
    const associatedPlatformIds = this.getAssociatedMitigationPlatforms(deviceID, [
      MITIGATION_TYPES.ADAPTIVE_FLOWSPEC,
      MITIGATION_TYPES.FLOWSPEC
    ]).map((platform) => platform.id);

    return this.collection.filter((model) => associatedPlatformIds.includes(model.get('platformID')));
  }

  getPoliciesUsingPlatform(platformId) {
    return this.store.$alerting.policyCollection.unfiltered.filter((model) =>
      model.mitigations.some((mitigation) => mitigation.platformID === platformId)
    );
  }

  getPoliciesUsingMethod(methodId, platformId) {
    return this.store.$alerting.policyCollection.unfiltered.filter((model) => {
      if (platformId) {
        return model.mitigations.some(
          (mitigation) => mitigation.methodID === methodId && mitigation.platformID === platformId
        );
      }

      return model.mitigations.some((mitigation) => mitigation.methodID === methodId);
    });
  }

  getAnnouncementsForMitigation(mitigationId) {
    return this.announcementCollection.unfiltered.filter((model) => model.mitigationIds.includes(mitigationId));
  }

  @computed
  get allUsedPlatformsAndMethods() {
    if (!this.allMitigationsCollection) {
      this.allMitigationsCollection = new MitigationCollection();
      this.allMitigationsCollection.serverFilter = {
        states: [],
        lookback: 24 * 90 * 3600
      };
    }

    return this.allMitigationsCollection.queuedFetch().then(() => this.allMitigationsCollection.platformMethodOptions);
  }
}

export default new MitigationsStore();
